import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { getChurnSurveys, ChurnSurvey, ChurnSurveyStats } from '../adminApi';

const REASON_LABELS: Record<string, string> = {
  too_expensive: 'Too expensive',
  didnt_use_enough: "Didn't use enough",
  missing_features: 'Missing features',
  plans_didnt_fit: "Plans didn't fit",
  found_alternative: 'Found alternative',
  just_exploring: 'Just exploring',
};

const REASON_COLORS: Record<string, 'error' | 'warning' | 'info' | 'default'> = {
  too_expensive: 'error',
  didnt_use_enough: 'warning',
  missing_features: 'info',
  plans_didnt_fit: 'warning',
  found_alternative: 'error',
  just_exploring: 'default',
};

const ChurnSurveys: React.FC = () => {
  const [surveys, setSurveys] = useState<ChurnSurvey[]>([]);
  const [stats, setStats] = useState<ChurnSurveyStats[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSurveys = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getChurnSurveys();
      setSurveys(data.surveys);
      setStats(data.stats);
      setTotal(data.total);
    } catch (err: any) {
      setError(err.message || 'Failed to load surveys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSurveys();
  }, [fetchSurveys]);

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Churn Surveys
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={fetchSurveys} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Stats summary */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
          Reason Breakdown ({total} total responses)
        </Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          {stats.map((s) => (
            <Chip
              key={s.reason}
              label={`${REASON_LABELS[s.reason] || s.reason}: ${s.count}`}
              color={REASON_COLORS[s.reason] || 'default'}
              variant="outlined"
              size="small"
            />
          ))}
          {stats.length === 0 && !loading && (
            <Typography variant="body2" color="text.secondary">
              No responses yet
            </Typography>
          )}
        </Stack>
      </Paper>

      {/* Survey table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell>Date</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Reason</TableCell>
              <TableCell>Feedback</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : surveys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                  No survey responses yet
                </TableCell>
              </TableRow>
            ) : (
              surveys.map((survey) => (
                <TableRow key={survey.id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {formatDate(survey.created_at)}
                  </TableCell>
                  <TableCell>{survey.email}</TableCell>
                  <TableCell>
                    <Chip
                      label={REASON_LABELS[survey.reason] || survey.reason}
                      color={REASON_COLORS[survey.reason] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ maxWidth: 400 }}>
                    {survey.feedback ? (
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {survey.feedback}
                      </Typography>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        -
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ChurnSurveys;
